/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/pages/Records.jsx
 * Description  : Records list page with alert buttons
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getRecords, importFile, sendAlert, sendAnnualAlert, updateRecordType, clearRecord, adjustClearRecord, deleteRecord, updateRecordNote, getSettings } from '../api';
import { useToast } from '../App';
import Modal from '../components/Modal';

const THAI_MONTHS = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

function thaiDate(str) {
  if (!str) return '-';
  try {
    const [y, m, d] = str.split('-').map(Number);
    return `${d} ${THAI_MONTHS[m]} ${y + 543}`;
  } catch { return str; }
}

function daysSince(dateStr) {
  if (!dateStr) return 0;
  const reserve = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  reserve.setHours(0, 0, 0, 0);
  return Math.floor((today - reserve) / 86400000);
}

function alertTooltipDate(dateStr) {
  if (!dateStr) return null;
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return `${d} ${THAI_MONTHS[m]} ${y + 543}`;
  } catch { return null; }
}

function addDaysDate(dateStr, days) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + days);
    const m = d.getMonth() + 1;
    return `${d.getDate()} ${THAI_MONTHS[m]} ${d.getFullYear() + 543}`;
  } catch { return null; }
}

function AlertButton({ n, record, thresholds, onSend }) {
  const sentKey = `alert${n}_sent`;
  const dateKey = `alert${n}_date`;
  const prevSentKey = n > 1 ? `alert${n - 1}_sent` : null;
  const isSent = !!record[sentKey];
  const prevSent = prevSentKey ? !!record[prevSentKey] : true;

  const dateLabel = isSent
    ? alertTooltipDate(record[dateKey])
    : addDaysDate(record.reserve_date, thresholds[n - 1] - 1);

  let btnClass = '';
  let icon = null;
  let disabled = false;
  let title = `Alert ${n}: ${dateLabel || '-'}`;

  if (isSent) {
    btnClass = 'bg-primary text-white cursor-default';
    icon = '✓';
    disabled = true;
  } else if (!prevSent) {
    btnClass = 'bg-gray-100 text-gray-400 cursor-not-allowed';
    icon = '🔒';
    disabled = true;
    title = `ต้องส่ง Alert ${n - 1} ก่อน`;
  } else {
    btnClass = 'bg-warning text-white hover:bg-yellow-600 cursor-pointer';
    icon = n;
    disabled = false;
  }

  return (
    <button
      onClick={() => !disabled && onSend(record, n)}
      disabled={disabled}
      title={title}
      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${btnClass}`}
    >
      {icon}
    </button>
  );
}

// Same shape as AlertButton, but for "สำรองปี" records — due dates are a
// fixed day-of-month in December (annualDays[n-1]) of the record's own
// reserve_date year, not N days elapsed from it.
function AnnualAlertButton({ n, record, annualDays, onSend }) {
  const sentKey = `annual_alert${n}_sent`;
  const dateKey = `annual_alert${n}_date`;
  const prevSentKey = n > 1 ? `annual_alert${n - 1}_sent` : null;
  const isSent = !!record[sentKey];
  const prevSent = prevSentKey ? !!record[prevSentKey] : true;
  const year = record.reserve_date ? new Date(record.reserve_date).getFullYear() : null;

  const dateLabel = isSent
    ? alertTooltipDate(record[dateKey])
    : (year != null ? `${annualDays[n - 1]} ธ.ค. ${year + 543}` : '-');

  let btnClass = '';
  let icon = null;
  let disabled = false;
  let title = `สำรองปี ${n}: ${dateLabel || '-'}`;

  if (isSent) {
    btnClass = 'bg-indigo-600 text-white cursor-default';
    icon = '✓';
    disabled = true;
  } else if (!prevSent) {
    btnClass = 'bg-gray-100 text-gray-400 cursor-not-allowed';
    icon = '🔒';
    disabled = true;
    title = `ต้องส่ง Alert ${n - 1} ก่อน`;
  } else {
    btnClass = 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 cursor-pointer';
    icon = n;
    disabled = false;
  }

  return (
    <button
      onClick={() => !disabled && onSend(record, n)}
      disabled={disabled}
      title={title}
      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${btnClass}`}
    >
      {icon}
    </button>
  );
}

export default function Records({ onRefresh, setPendingCount }) {
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [thresholds, setThresholds] = useState([5, 10, 15]);
  const [annualDays, setAnnualDays] = useState([1, 15, 25]);
  const [confirmAlert, setConfirmAlert] = useState(null); // { record, n, kind: 'normal'|'annual' }
  const [typeModal, setTypeModal] = useState(null);
  const [changingType, setChangingType] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [clearModal, setClearModal] = useState(null);
  const [clearInput, setClearInput] = useState('');
  const [clearMode, setClearMode] = useState('add');   // 'add' | 'edit'
  const [adjustInput, setAdjustInput] = useState('');
  const [noteModal, setNoteModal] = useState(null);
  const [noteInput, setNoteInput] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const fileRef = useRef();
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const cleared = filter === 'all' ? 'all' : filter === 'cleared' ? '1' : '0';
      const [recRes, settRes] = await Promise.all([
        getRecords(cleared),
        getSettings(),
      ]);
      setRecords(recRes.data);
      const s = settRes.data;
      setThresholds([
        parseInt(s.alert1_days || 5),
        parseInt(s.alert2_days || 10),
        parseInt(s.alert3_days || 15),
      ]);
      setAnnualDays([
        parseInt(s.annual_alert1_day || 1),
        parseInt(s.annual_alert2_day || 15),
        parseInt(s.annual_alert3_day || 25),
      ]);
      const pending = filter === 'all'
        ? recRes.data.filter((r) => r.cleared < 2).length
        : filter === 'pending'
        ? recRes.data.length
        : 0;
      setPendingCount(pending);
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'โหลดข้อมูลไม่สำเร็จ');
      }
    } finally {
      setLoading(false);
    }
  }, [filter, addToast, setPendingCount]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setPage(1); }, [filter, pageSize]);

  const handleImport = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setImporting(true);
    let totalImported = 0;
    const errors = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await importFile(fd);
        totalImported += res.data.imported || 0;
      } catch (err) {
        if (!err.response) {
          errors.push(`${file.name}: ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้`);
        } else {
          errors.push(`${file.name}: ${err.response?.data?.detail || 'นำเข้าไม่สำเร็จ'}`);
        }
      }
    }
    if (totalImported > 0) {
      addToast('success', `นำเข้าสำเร็จ ${files.length} ไฟล์ รวม ${totalImported} รายการ`);
      loadData();
      onRefresh();
    }
    if (errors.length) {
      addToast('error', errors.join('\n'));
    }
    setImporting(false);
    e.target.value = '';
  };

  const handleSend = (record, n, kind = 'normal') => {
    setConfirmAlert({ record, n, kind });
  };

  const confirmSend = async () => {
    const { record, n, kind } = confirmAlert;
    setConfirmAlert(null);
    try {
      if (kind === 'annual') await sendAnnualAlert(record.id, n);
      else await sendAlert(record.id, n);
      addToast('success', `ส่ง Alert ${n} ถึง ${record.name} สำเร็จ`);
      loadData();
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || `ส่ง Alert ${n} ไม่สำเร็จ`);
      }
    }
  };

  const handleConfirmType = async () => {
    if (!typeModal) return;
    const newType = typeModal.reserve_type === 'annual' ? 'normal' : 'annual';
    setChangingType(true);
    try {
      await updateRecordType(typeModal.id, newType);
      addToast('success', `เปลี่ยนประเภท ${typeModal.name} เป็น "${newType === 'annual' ? 'สำรองปี' : 'ปกติ'}" สำเร็จ`);
      setTypeModal(null);
      loadData();
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || 'เปลี่ยนประเภทไม่สำเร็จ');
      }
    } finally {
      setChangingType(false);
    }
  };

  const openClearModal = (record) => {
    const remaining = record.amount - (record.cleared_amount || 0);
    setClearModal(record);
    setClearInput(remaining.toFixed(2));
    setClearMode('add');
    setAdjustInput((parseFloat(record.cleared_amount) || 0).toFixed(2));
  };

  const handleClear = async () => {
    if (!clearModal) return;
    const amount = parseFloat(clearInput);
    if (isNaN(amount) || amount <= 0) {
      addToast('error', 'กรุณากรอกยอดที่ถูกต้อง');
      return;
    }
    try {
      await clearRecord(clearModal.id, amount);
      const remaining = clearModal.amount - (clearModal.cleared_amount || 0);
      const isFullClear = amount >= remaining;
      addToast('success', isFullClear
        ? `เคลียร์ทั้งหมด ${clearModal.name} สำเร็จ`
        : `เคลียร์บางส่วน ${clearModal.name} ยอด ${amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท สำเร็จ`
      );
      setClearModal(null);
      loadData();
      onRefresh();
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || 'เคลียร์ไม่สำเร็จ');
      }
    }
  };

  const handleConfirmClear = async () => {
    if (!clearModal) return;
    if (clearMode === 'edit') {
      const newAmount = parseFloat(adjustInput);
      if (isNaN(newAmount) || newAmount < 0) { addToast('error', 'กรุณากรอกยอดที่ถูกต้อง'); return; }
      try {
        await adjustClearRecord(clearModal.id, newAmount);
        addToast('success', `แก้ไขยอดเคลียร์ ${clearModal.name} สำเร็จ`);
        setClearModal(null);
        loadData(); onRefresh();
      } catch (err) { addToast('error', err.response?.data?.detail || 'แก้ไขไม่สำเร็จ'); }
    } else {
      await handleClear();
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteRecord(confirmDelete.id);
      addToast('success', 'ลบรายการสำเร็จ');
      setConfirmDelete(null);
      loadData();
      onRefresh();
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || 'ลบไม่สำเร็จ');
      }
    }
  };

  const openNoteModal = (record) => {
    setNoteModal(record);
    setNoteInput(record.note || '');
  };

  const handleSaveNote = async () => {
    if (!noteModal) return;
    setSavingNote(true);
    try {
      await updateRecordNote(noteModal.id, noteInput);
      addToast('success', `บันทึกหมายเหตุ ${noteModal.name} สำเร็จ`);
      setNoteModal(null);
      loadData();
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || 'บันทึกหมายเหตุไม่สำเร็จ');
      }
    } finally {
      setSavingNote(false);
    }
  };

  const tabs = [
    { key: 'pending', label: 'รอดำเนินการ' },
    { key: 'cleared', label: 'เคลียร์แล้ว' },
    { key: 'all', label: 'ทั้งหมด' },
  ];

  const totalPages  = Math.max(1, Math.ceil(records.length / pageSize));
  const safePage    = Math.min(page, totalPages);
  const pageRecords = records.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-800 flex-1">รายการสำรองจ่าย</h2>
        <button
          onClick={() => fileRef.current.click()}
          disabled={importing}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-60"
        >
          {importing ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          )}
          นำเข้าไฟล์ TXT
        </button>
        <input ref={fileRef} type="file" accept=".txt" multiple className="hidden" onChange={handleImport} />
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors
              ${filter === tab.key ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-4xl mb-3">📭</div>
            <div>ไม่พบรายการ</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left">ผู้สำรองจ่าย</th>
                  <th className="px-4 py-3 text-left">บริษัท</th>
                  <th className="px-4 py-3 text-right">ยอดเงิน</th>
                  <th className="px-4 py-3 text-center">วันที่สำรอง</th>
                  <th className="px-4 py-3 text-center">ประเภท</th>
                  <th className="px-4 py-3 text-center">Alert CSAF</th>
                  <th className="px-4 py-3 text-center">เคลียร์สำรอง</th>
                  <th className="px-4 py-3 text-center">หมายเหตุ</th>
                  <th className="px-4 py-3 text-center">ลบ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageRecords.map((r) => {
                  const days = daysSince(r.reserve_date);
                  const canClear = r.cleared < 2;
                  const remaining = parseFloat(r.amount || 0) - parseFloat(r.cleared_amount || 0);
                  const isPartial = r.cleared === 1;
                  const isFullCleared = r.cleared === 2;
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${isFullCleared ? 'opacity-60' : ''}`}>
                      {/* Name + email */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{r.name}</div>
                        <div className="text-xs text-gray-400">{r.email || '-'}</div>
                      </td>
                      {/* Company */}
                      <td className="px-4 py-3">
                        <div className="text-gray-700 text-sm">{r.company || '-'}</div>
                      </td>
                      {/* Amount + filename */}
                      <td className="px-4 py-3 text-right">
                        <div className="font-semibold text-gray-800">
                          {parseFloat(r.amount||0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
                        </div>
                        {isPartial && (
                          <div className="text-xs text-orange-500 font-medium mt-0.5">
                            คงเหลือ {remaining.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
                          </div>
                        )}
                        <div className="text-xs text-gray-400 truncate max-w-[120px] ml-auto">{r.filename}</div>
                      </td>
                      {/* Date + days badge (normal records only — a "สำรองปี"
                          record isn't tracked by days elapsed) */}
                      <td className="px-4 py-3 text-center">
                        <div className="text-gray-700">{thaiDate(r.reserve_date)}</div>
                        {!isFullCleared && r.reserve_type !== 'annual' && (
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 font-medium
                            ${days >= thresholds[2] ? 'bg-red-100 text-danger' :
                              days >= thresholds[1] ? 'bg-orange-100 text-orange-600' :
                              days >= thresholds[0] ? 'bg-yellow-100 text-warning' :
                              'bg-gray-100 text-gray-500'}`}
                          >
                            {days} วัน
                          </span>
                        )}
                      </td>
                      {/* Type — click to toggle ปกติ / สำรองปี */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setTypeModal(r)}
                          title="คลิกเพื่อเปลี่ยนประเภทการแจ้งเตือน"
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            r.reserve_type === 'annual'
                              ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {r.reserve_type === 'annual' ? '📅 สำรองปี' : 'ปกติ'}
                        </button>
                      </td>
                      {/* Alert buttons — annual (December calendar-date)
                          schedule replaces the normal (days-elapsed) one */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {r.reserve_type === 'annual'
                            ? [1, 2, 3].map((n) => (
                                <AnnualAlertButton
                                  key={n}
                                  n={n}
                                  record={r}
                                  annualDays={annualDays}
                                  onSend={(rec, num) => handleSend(rec, num, 'annual')}
                                />
                              ))
                            : [1, 2, 3].map((n) => (
                                <AlertButton
                                  key={n}
                                  n={n}
                                  record={r}
                                  thresholds={thresholds}
                                  onSend={handleSend}
                                />
                              ))}
                        </div>
                      </td>
                      {/* Clear button */}
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {isFullCleared ? (
                            <button
                              onClick={() => openClearModal(r)}
                              title="แก้ไขยอดเคลียร์"
                              className="text-xs text-success font-medium hover:underline"
                            >
                              ✓ เคลียร์ทั้งหมด
                            </button>
                          ) : isPartial ? (
                            <button
                              onClick={() => openClearModal(r)}
                              title="เคลียร์เพิ่มเติม / แก้ไขยอด"
                              className="px-3 py-1 rounded-lg text-xs font-medium transition-colors bg-orange-500 text-white hover:bg-orange-600"
                            >
                              เคลียร์เพิ่ม
                            </button>
                          ) : (
                            <button
                              onClick={() => openClearModal(r)}
                              title="เคลียร์รายการนี้"
                              className="px-3 py-1 rounded-lg text-xs font-medium transition-colors bg-success text-white hover:bg-green-700"
                            >
                              เคลียร์
                            </button>
                          )}
                        </div>
                      </td>
                      {/* Note */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => openNoteModal(r)}
                          title={r.note ? r.note : 'เพิ่มหมายเหตุ'}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg mx-auto transition-colors ${
                            r.note ? 'text-primary bg-primary/10 hover:bg-primary/20' : 'text-gray-400 hover:bg-gray-100'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </td>
                      {/* Delete */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setConfirmDelete(r)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-danger transition-colors mx-auto"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination bar */}
        {!loading && records.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span>แสดง {records.length === 0 ? 0 : (safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, records.length)} จาก {records.length} รายการ</span>
              <span className="text-gray-300">|</span>
              <span>แสดง</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
              >
                {[5, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>รายการ/หน้า</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={safePage === 1}
                className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="หน้าแรก"
              >«</button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >‹</button>

              {/* Page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`e${i}`} className="px-2">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded text-xs font-medium transition-colors
                        ${p === safePage ? 'bg-primary text-white' : 'hover:bg-gray-100'}`}
                    >
                      {p}
                    </button>
                  )
                )}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >›</button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={safePage === totalPages}
                className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="หน้าสุดท้าย"
              >»</button>
            </div>
          </div>
        )}
      </div>

      {/* Clear / Adjust modal */}
      {clearModal && (() => {
        const totalAmount    = parseFloat(clearModal.amount || 0);
        const alreadyCleared = parseFloat(clearModal.cleared_amount || 0);
        const remaining      = totalAmount - alreadyCleared;
        const hasCleared     = alreadyCleared > 0;

        // mode: add
        const addVal      = parseFloat(clearInput);
        const addValid    = !isNaN(addVal) && addVal > 0 && addVal <= remaining;
        const willFull    = addValid && addVal >= remaining;

        // mode: edit
        const editVal     = parseFloat(adjustInput);
        const editValid   = !isNaN(editVal) && editVal >= 0 && editVal <= totalAmount && editVal !== alreadyCleared;
        const editStatus  = isNaN(editVal) ? '' : editVal <= 0 ? 'รีเซ็ตเป็นรอดำเนินการ' : editVal >= totalAmount ? 'เคลียร์ทั้งหมด' : `เคลียร์บางส่วน (คงเหลือ ${(totalAmount - editVal).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท)`;

        const isValid   = clearMode === 'add' ? addValid : editValid;
        const btnColor  = clearMode === 'edit' ? 'bg-blue-600 hover:bg-blue-700' : willFull ? 'bg-success hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600';
        const btnLabel  = clearMode === 'edit' ? 'บันทึกการแก้ไข' : willFull ? 'ยืนยันเคลียร์ทั้งหมด' : 'ยืนยันเคลียร์บางส่วน';
        const title     = clearMode === 'edit' ? 'แก้ไขยอดที่เคลียร์ไปแล้ว' : clearModal.cleared === 1 ? 'เคลียร์ยอดเพิ่มเติม' : 'เคลียร์ยอดสำรอง';

        return (
          <Modal
            title={title}
            onClose={() => setClearModal(null)}
            footer={
              <>
                <button onClick={() => setClearModal(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">ยกเลิก</button>
                <button
                  onClick={handleConfirmClear}
                  disabled={!isValid}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${isValid ? btnColor : 'bg-gray-300 cursor-not-allowed'}`}
                >
                  {btnLabel}
                </button>
              </>
            }
          >
            <div className="space-y-3 text-sm">
              {/* Info box */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <div className="font-semibold text-gray-800">{clearModal.name}</div>
                <div className="text-gray-500 text-xs">{clearModal.email}</div>
                <div className="flex justify-between mt-2 pt-2 border-t border-gray-200">
                  <span className="text-gray-500">ยอดทั้งหมด</span>
                  <span className="font-medium">{totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท</span>
                </div>
                {hasCleared && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">เคลียร์ไปแล้ว</span>
                    <span className="text-success font-medium">{alreadyCleared.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท</span>
                  </div>
                )}
                {hasCleared && (
                  <div className="flex justify-between font-semibold">
                    <span className="text-orange-600">ยอดคงเหลือ</span>
                    <span className="text-orange-600">{remaining.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท</span>
                  </div>
                )}
              </div>

              {/* Toggle tabs (แสดงเฉพาะเมื่อมียอดเคลียร์แล้ว) */}
              {hasCleared && (
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                  {[{ key: 'add', label: 'เคลียร์เพิ่ม' }, { key: 'edit', label: 'แก้ไขยอดเคลียร์' }].map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setClearMode(t.key)}
                      className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors
                        ${clearMode === t.key ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Add mode */}
              {clearMode === 'add' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">ยอดที่ต้องการเคลียร์ครั้งนี้ (บาท)</label>
                  <input
                    type="number" value={clearInput} onChange={(e) => setClearInput(e.target.value)}
                    min="0.01" max={remaining} step="0.01" autoFocus
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {addValid && !willFull && <p className="text-xs text-orange-500 mt-1">หลังเคลียร์จะยังคงเหลือ {(remaining - addVal).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท</p>}
                  {willFull && <p className="text-xs text-success mt-1">✓ จะเคลียร์ครบทั้งหมด</p>}
                </div>
              )}

              {/* Edit mode */}
              {clearMode === 'edit' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">ยอดที่เคลียร์ที่ถูกต้อง (บาท)</label>
                  <input
                    type="number" value={adjustInput} onChange={(e) => setAdjustInput(e.target.value)}
                    min="0" max={totalAmount} step="0.01" autoFocus
                    className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  {editValid && <p className={`text-xs mt-1 font-medium ${editVal <= 0 ? 'text-gray-500' : editVal >= totalAmount ? 'text-success' : 'text-orange-500'}`}>→ {editStatus}</p>}
                  {!editValid && adjustInput !== '' && parseFloat(adjustInput) > totalAmount && (
                    <p className="text-xs mt-1 text-danger">ยอดเกินยอดสำรองทั้งหมด ({totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท)</p>
                  )}
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* Confirm send alert modal (normal or annual) */}
      {confirmAlert && (
        <Modal
          title={`ยืนยันการส่ง Alert ${confirmAlert.n}${confirmAlert.kind === 'annual' ? ' (สำรองปี)' : ''}`}
          onClose={() => setConfirmAlert(null)}
          footer={
            <>
              <button
                onClick={() => setConfirmAlert(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
              >
                ยกเลิก
              </button>
              <button
                onClick={confirmSend}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${confirmAlert.kind === 'annual' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-warning hover:bg-yellow-600'}`}
              >
                ยืนยันส่ง Alert {confirmAlert.n}
              </button>
            </>
          }
        >
          <div className="space-y-2 text-sm">
            <p>คุณต้องการส่ง <strong>Alert {confirmAlert.n}{confirmAlert.kind === 'annual' ? ' (สำรองปี)' : ''}</strong> ถึง</p>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-semibold">{confirmAlert.record.name}</div>
              <div className="text-gray-500">{confirmAlert.record.email}</div>
              <div className="text-gray-700 mt-1">
                ยอด {parseFloat(confirmAlert.record.amount||0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
              </div>
            </div>
            <p className="text-gray-500">ระบบจะส่งอีเมลแจ้งเตือนไปยังผู้สำรองจ่าย</p>
          </div>
        </Modal>
      )}

      {/* Confirm type-change modal */}
      {typeModal && (
        <Modal
          title="เปลี่ยนประเภทการแจ้งเตือน"
          onClose={() => setTypeModal(null)}
          footer={
            <>
              <button
                onClick={() => setTypeModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleConfirmType}
                disabled={changingType}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium disabled:opacity-60"
              >
                {changingType ? 'กำลังบันทึก...' : 'ยืนยันเปลี่ยนประเภท'}
              </button>
            </>
          }
        >
          <div className="space-y-2 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-semibold text-gray-800">{typeModal.name}</div>
              <div className="text-gray-500 text-xs">{typeModal.email}</div>
            </div>
            {typeModal.reserve_type === 'annual' ? (
              <p>เปลี่ยนจาก <strong className="text-indigo-600">📅 สำรองปี</strong> กลับเป็น <strong>ปกติ</strong> — จะใช้การแจ้งเตือนแบบนับวัน (5/10/15 วัน) ตามปกติ</p>
            ) : (
              <p>เปลี่ยนจาก <strong>ปกติ</strong> เป็น <strong className="text-indigo-600">📅 สำรองปี</strong> — จะไม่ส่ง Alert แบบนับวันอีกต่อไป แต่จะแจ้งเตือนในเดือนธันวาคมของปี {new Date(typeModal.reserve_date).getFullYear() + 543} (ปีของวันที่สำรองจ่าย) แทน</p>
            )}
            <p className="text-gray-400 text-xs">ประวัติการส่ง Alert ที่ส่งไปแล้วของทั้งสองประเภทจะยังคงอยู่ ไม่ถูกล้าง</p>
          </div>
        </Modal>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <Modal
          title="ยืนยันการลบรายการ"
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-danger text-white hover:bg-red-700 text-sm font-medium"
              >
                ลบรายการ
              </button>
            </>
          }
        >
          <p className="text-sm">
            คุณต้องการลบรายการของ <strong>{confirmDelete.name}</strong> ยอด{' '}
            <strong>{parseFloat(confirmDelete.amount||0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท</strong> ใช่หรือไม่?
            <br />
            <span className="text-danger text-xs">การดำเนินการนี้ไม่สามารถยกเลิกได้</span>
          </p>
        </Modal>
      )}

      {/* Note modal */}
      {noteModal && (
        <Modal
          title="หมายเหตุเพิ่มเติม"
          onClose={() => setNoteModal(null)}
          footer={
            <>
              <button
                onClick={() => setNoteModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleSaveNote}
                disabled={savingNote}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark text-sm font-medium disabled:opacity-60"
              >
                {savingNote ? 'กำลังบันทึก...' : 'บันทึกหมายเหตุ'}
              </button>
            </>
          }
        >
          <div className="space-y-2 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-semibold text-gray-800">{noteModal.name}</div>
              <div className="text-gray-500 text-xs">{noteModal.email}</div>
            </div>
            <label className="block text-xs text-gray-500 mb-1">หมายเหตุ (พิมพ์ได้หลายบรรทัด)</label>
            <textarea
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              rows={6}
              autoFocus
              placeholder="พิมพ์หมายเหตุเพิ่มเติม..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
